import { PlatformApiError } from '../../api/client.ts';
import { coerceErrorMessage } from '../../components/Toast.tsx';
import type { I18nKey } from '../../i18n/index.ts';

/**
 * Localize the handful of auth failures that are a normal part of the
 * flow rather than a defect (epic #1475).
 *
 * `coerceErrorMessage` renders `CODE: english message` straight off the
 * wire, which is the right default for anything unexpected — it keeps the
 * server's own diagnostic visible. But "your code expired" and "this
 * invite code is out of slots today" are things a Chinese-speaking
 * customer is *supposed* to hit, and `INVITE_CODE_QUOTA_EXHAUSTED: this
 * invite code has no activation slots left today` is not an answer for
 * them. Callers pass the codes they have real copy for; everything else
 * falls through to the raw form on purpose, so an unmapped code is
 * legible in a bug report instead of hidden behind a generic apology.
 */

export type AuthErrorKeys = Readonly<Record<string, I18nKey>>;

export function localizeAuthError(
  err: unknown,
  translate: (key: I18nKey) => string,
  keys: AuthErrorKeys,
): string {
  if (err instanceof PlatformApiError) {
    const key = keys[err.code];
    if (key !== undefined) {
      return translate(key);
    }
  }
  return coerceErrorMessage(err);
}

/** True when the failure is specifically "this email already has an account". */
export function isAlreadyRegistered(err: unknown): boolean {
  return err instanceof PlatformApiError && err.code === 'EMAIL_ALREADY_REGISTERED';
}
